(ns practitest.frontend.components.view-editor
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.column-picker :as column-picker]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.selector2 :as selector2]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components.view-editor.basic-builder :as bb]
    [practitest.frontend.components.view-editor.events :as events]
    [practitest.frontend.components.view-editor.subs :as view-editor.subs]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.components-provider :refer [application-provider]]
    [practitest.frontend.components2.tabs :refer [tab tabs-container]]
    [practitest.frontend.pt-ts-bridge :as pt-ts-bridge]
    [practitest.frontend.ptql-editor :as ptql-editor]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; ---- views ----

(defn- track-action! [action]
  (mixpanel/track "Filter Editor" {"Action Name" action}))

(defn- track-toggle! [expanded-action collapsed-action]
  (fn [e]
    (track-action! (if (.. e -target -open) expanded-action collapsed-action))))

(defn- tooltip-icon [text]
  [tooltip/mux-tooltip
   {}
   text
   (fn [props]
     [:span.view-editor-tooltip-icon props
      [icons/mux-info-circle-nofill]])])

;; TODO: replace with non-bootstrap component once we get rid of bootstrap
(defn- view-as-ptql-switch [form]
  (let [render-mode @(re-frame/subscribe [::view-editor.subs/filter-render-mode])
        ptql-error @(re-frame/subscribe [::view-editor.subs/ptql-error])
        can-basic? (and (nil? ptql-error) (bb/fits? (:view-ast form)))
        ptql? (or (= :advanced render-mode) (not can-basic?))]
    [:div.form-switch.view-editor-render-mode-switch
       [:label.form-check-label {:for "view-editor-as-ptql"}
        (get-translation "view-editor.render-mode.ptql-mode" "PTQL Mode")]
       [:input.form-check-input
        {:type "checkbox"
         :id "view-editor-as-ptql"
         :checked ptql?
         :disabled (not can-basic?)
         :on-change (fn [e]
                      (let [advanced? (dom/event-value e)]
                        (track-action! (if advanced? "PTQL Mode" "Basic Mode"))
                        (re-frame/dispatch [::events/set-filter-render-mode
                                            (if advanced? :advanced :basic)])))}]
       (when-not can-basic?
         [tooltip-icon
          (get-translation "view-editor.render-mode.basic-unavailable.tooltip"
                           "Basic mode isn't available for this filter")])]))

(defn- ptql-section [{:keys [value on-change on-parse-result
                             parse-error read-only? auto-focus? source-entity]}]
  (let [fields-by-entity @(re-frame/subscribe [::subs/fields-by-ptql-entity])
        users @(re-frame/subscribe [::subs/users])
        groups @(re-frame/subscribe [::subs/groups])
        tags @(re-frame/subscribe [::subs/tags])]
    [:div.view-editor-section
     {:class (if read-only? "is-read-only" "is-editable")}
     [ptql-editor/editor
      {:value value
       :on-change on-change
       :on-parse-result on-parse-result
       :fields-by-entity fields-by-entity
       :source-entity source-entity
       :users users
       :groups groups
       :tags (mapv first tags)
       :read-only? read-only?
       :auto-focus? auto-focus?}]
     (when-not read-only?
       [:div.view-editor-ptql-hint
        [:kbd "Ctrl"] " + " [:kbd "Space"]
        " for suggestions"])
     (when parse-error
       [:div.view-editor-error-note parse-error])]))

(defn- auto-filter-row [idx value options removable?]
  [:div.auto-filter-row
   (when (pos? idx)
     [:span.auto-filter-row-indent [icons/mux-chevron-right]])
   [selector2/new-select
    {:items options
     :value (some #(when (= (:value %) value) %) options)
     :label-fn :label
     :key-fn :value
     :placeholder (get-translation "view-editor.auto-filter.none" "Select List Field")
     :on-change #(re-frame/dispatch [::events/set-auto-filter-field idx (:value %)])}]
   (when (= value "linked_milestones")
     [tooltip-icon (get-translation "view-editor.auto-filter.milestone-note.tooltip"
                                    "The milestone index shows up to 50 milestones based on their dates - active ones first, then upcoming and completed ones.")])
   (when removable?
     [buttons/icon-button
      {:icon [icons/mux-x]
       :tooltip (get-translation "generic.button.remove" "Remove")
       :on-click #(re-frame/dispatch [::events/remove-auto-filter-level idx])}])])

(defn- auto-filter-section [form]
  (let [options @(re-frame/subscribe [::view-editor.subs/auto-filter-options])
        max-levels @(re-frame/subscribe [::view-editor.subs/auto-filter-max-levels])
        levels (or (:auto-filter form) [])
        levels (if (empty? levels) [nil] levels)
        last-filled? (not (str/blank? (last levels)))
        can-add? (and last-filled? (< (count levels) max-levels))]
    [:div.view-editor-auto-filter-section
     (doall
       (map-indexed
         (fn [idx v]
           ^{:key idx} [auto-filter-row idx v options (and (pos? idx) (= idx (dec (count levels))))])
         levels))
     (when can-add?
       [buttons/button
        {:tertiary? true
         :icon [icons/mux-reg-plus]
         :text (get-translation "view-editor.auto-filter.add-child" "Add Child Auto Filter")
         :on-click #(re-frame/dispatch [::events/add-auto-filter-level])}])
     [:div.view-editor-auto-filter-note
      [:span (get-translation "view-editor.auto-filter.values-limit-note"
                              "Note: Each Auto Filter is limited to 50 values. If the selected field contains more than 50 values, only the 50 latest values will be used.")]]
     [:label.m-checkbox-label
      [:input {:type "checkbox"
               :checked (boolean (:hide-empty-auto-filters form))
               :on-change #(re-frame/dispatch [::events/set-hide-empty-auto-filters (dom/event-value %)])}]
      (get-translation "view-editor.auto-filter.hide-empty" "Hide empty values in Filter")
      [:span {:on-click dom/prevent-default}
       [tooltip-icon
        (get-translation "view-editor.auto-filter.hide-empty.tooltip"
                         "Don't show auto-filter values that have no matching entities.")]]]]))

(defn- name-row [form mode]
  [:div.view-editor-name-row
   [:div.view-editor-name-field
    [:label.view-editor-name-label {:for "view-editor-name"}
     (get-translation "view-editor.name" "Filter Name")]
    [:input.m-input
     {:id "view-editor-name"
      :type "text"
      :auto-focus (= mode :create)
      :value (:name form)
      :on-change #(re-frame/dispatch [::events/name-changed (dom/event-value %)])}]]
   [:fieldset.view-editor-visibility
    [:legend.view-editor-visibility-label
     (get-translation "view-editor.visibility" "Visibility")]
    [:div.view-editor-visibility-options
     [:label.view-editor-visibility-option
      [:input
       {:type "radio"
        :name "view-editor-visibility"
        :checked (not (boolean (:public? form)))
        :on-change #(do (track-action! "Private Selection")
                        (re-frame/dispatch [::events/public-changed false]))}]
      " "
      (get-translation "view-editor.is-private" "Private")]
     [:label.view-editor-visibility-option
      [:input
       {:type "radio"
        :name "view-editor-visibility"
        :checked (boolean (:public? form))
        :on-change #(do (track-action! "Public Selection")
                        (re-frame/dispatch [::events/public-changed true]))}]
      " "
      (get-translation "view-editor.is-public" "Public")]]]])

(defn- filter-type-tabs [form]
  (let [limit-reached? @(re-frame/subscribe [::view-editor.subs/auto-filter-limit-reached?])
        af-mode? (= :auto-filter (:mode form))
        af-toggle-disabled? (and limit-reached? (not af-mode?))]
    [:div.view-editor-filter-conditions
     [:span.view-editor-section-label.view-editor-filter-conditions-label
      (get-translation "view-editor.filter-conditions" "Filter Conditions")]
     [:div.view-editor-filter-type
      [tabs-container {:active-tab-id (or (:mode form) :ptql)
                       :on-toggle #(re-frame/dispatch [::events/set-mode %])}
       [tab {:tab-id :auto-filter}
        (get-translation "view-editor.filter-type.auto-filter" "Auto Filter")]
       [tab {:tab-id :ptql}
        (get-translation "view-editor.filter-type.manual" "Manual")]]]
     (when af-toggle-disabled?
       [:div.view-editor-filter-type-warning
        (get-translation "view-editor.auto-filter.limit-reached"
                         "Auto-filter limit reached for this entity type")])]))


(defn- ancestors-summary [level-count]
  [:summary.view-editor-ancestors-summary
   [:span.view-editor-chevron-icon [icons/mux-chevron-right]]
   [:span (get-translation "view-editor.ancestors.toggle" "Parent Filters")]
   (when (pos? level-count)
     [:span.view-editor-ancestors-level-count
      " | " level-count " "
      (get-translation "view-editor.ancestors.parent-levels" "Parent Levels")])])

(defn- parent-filter-picker [project-id user parent-id parent-name full-path source-entity]
  [tooltip/mux-tooltip
   {:placement "top"}
   full-path
   (fn [props]
     [:div props
      [application-provider
       [:> pt-ts-bridge/GenericSearchableTree
        {:customClassName "parent-searchable-tree"
         :excludeAutoFilters true
         :scope (clj->js {:userId (str (:id user))
                          :projectId (str project-id)
                          :module source-entity
                          :entity source-entity
                          :tab "filters"})
         :urls (clj->js {:fetchRootTreeUrl (str "/projects/" project-id "/custom_views/left_pane_views?entity_name=" source-entity)
                         :fetchTreeNode (str "/projects/" project-id "/custom_views/[id]/fetch_filter?entity_name=" source-entity)
                         :search (str "/projects/" project-id "/custom_views/search?entity_name=" source-entity)})
         :initialSelectedItem (when parent-id
                                #js {:id (str parent-id) :name (or parent-name "")})
         :selectedValue (when parent-id (str parent-id))
         :displayName full-path
         :onChange (fn [server-obj]
                     (track-action! "Parent Filter Selected")
                     (re-frame/dispatch
                       [::events/set-parent-filter
                        (js->clj server-obj :keywordize-keys true)]))}]]])])

(defn- ancestors-section [form source-entity]
  (let [project-id @(re-frame/subscribe [::subs/current-project-id])
        user @(re-frame/subscribe [::subs/current-user])
        parent-id @(re-frame/subscribe [::view-editor.subs/parent-id])
        level-count (count (:ancestors-path form))
        parent-name (:name (last (:ancestors-path form)))
        full-path (when (seq (:ancestors-path form))
                    (str/join " / " (map :name (:ancestors-path form))))]
    [:div.view-editor-ancestors-section
     [:span.view-editor-name-label
      (get-translation "view-editor.ancestors.toggle" "Parent Filters")]
     [:details.view-editor-ancestors-details
      {:on-toggle (track-toggle! "Parent Filters Expanded" "Parent Filters Collapsed")}
      [ancestors-summary level-count]
      [:div.view-editor-ancestors-content
       [parent-filter-picker project-id user parent-id parent-name full-path source-entity]
       [ptql-section
        {:value (or (:ancestors-ptql-optimized form) "")
         :read-only? true
         :source-entity source-entity}]]]]))


(defn- combined-preview [form source-entity]
  (let [af-mode? (= :auto-filter (:mode form))
        recomputing? @(re-frame/subscribe [::view-editor.subs/recomputing?])
        value (if af-mode?
                (:ancestors-ptql-optimized form)
                (:combined-ptql-optimized form))]
    [:details.view-editor-collapsible-card
     {:on-toggle (track-toggle! "Effective Filter Expanded" "Effective Filter Collapsed")}
     [:summary.view-editor-collapsible-summary
      [:span.view-editor-chevron-icon [icons/mux-chevron-right]]
      [:span.view-editor-collapsible-title
       (get-translation "view-editor.combined.label" "Effective filter")
       [tooltip-icon (get-translation "view-editor.combined.tooltip"
                                      "The effective filter combines this filter with all parent filters.")]]
      [:span.view-editor-collapsible-hint
       (if (and (not af-mode?) recomputing?)
         (get-translation "view-editor.combined-computing" "Computing...")
         (get-translation "view-editor.combined.read-only" "Read Only"))]]
     [ptql-section
      {:value (or value "")
       :read-only? true
       :source-entity source-entity}]]))

(defn- manual-filter-section [form mode source-entity]
  (let [render-mode @(re-frame/subscribe [::view-editor.subs/filter-render-mode])
        ptql-error @(re-frame/subscribe [::view-editor.subs/ptql-error])]
    (case render-mode
      :basic [bb/basic-filter-section form]
      :advanced [ptql-section
                 {:value (:view-ptql form)
                  :read-only? false
                  :auto-focus? (= mode :edit)
                  :on-change #(re-frame/dispatch [::events/view-ptql-changed %])
                  :on-parse-result #(re-frame/dispatch [::events/set-ptql-error %])
                  :parse-error ptql-error
                  :source-entity source-entity}])))

(defn- fields-section [form]
  (let [available-fields @(re-frame/subscribe [::view-editor.subs/available-fields])
        custom-columns? (seq (:definition form))]
    [:details.view-editor-collapsible-card
     {:on-toggle (track-toggle! "Columns Settings Expanded" "Columns Settings Collapsed")}
     [:summary.view-editor-collapsible-summary
      [:span.view-editor-chevron-icon [icons/mux-chevron-right]]
      [:span.view-editor-collapsible-title
       (get-translation "view-editor.default-fields" "Columns Settings")
       [tooltip-icon (get-translation "view-editor.default-fields.tooltip"
                                      "Choose which columns to display by default for this filter.")]]
      [:span.view-editor-collapsible-hint
       (if custom-columns?
         (get-translation "view-editor.default-fields.custom" "Custom Columns")
         (get-translation "view-editor.default-fields.default" "Using Default Columns"))]]
     [column-picker/column-picker
      {:empty-hint (get-translation "view-editor.default-fields.empty-hint"
                                    "No columns selected - the project's default columns for this entity type will be used.")
       :available-fields available-fields
       :selected (or (:definition form) [])
       :on-change #(re-frame/dispatch [::events/set-definition %])}]]))

(defn- view-editor-body []
  (let [form @(re-frame/subscribe [::view-editor.subs/form])
        loading? @(re-frame/subscribe [::view-editor.subs/loading?])
        mode @(re-frame/subscribe [::view-editor.subs/mode])
        source-entity @(re-frame/subscribe [::view-editor.subs/entity-type])
        af-mode? (= :auto-filter (:mode form))]
    (if loading?
      [:div.view-editor-loading (get-translation "view-editor.loading" "Loading...")]
      [:div.view-editor-body
       [name-row form mode]
       (when source-entity [ancestors-section form source-entity])
       [:div.view-editor-filter-section-container
        [filter-type-tabs form]
        (if af-mode?
          [auto-filter-section form]
          [:div.view-editor-filter-section-container
           [view-as-ptql-switch form]
           [manual-filter-section form mode source-entity]])]
       [:hr.view-editor-divider]
       [:div.view-editor-collapsibles-container
        [combined-preview form source-entity]
        [fields-section form]]])))

(defn view-editor-modal []
  (let [open? @(re-frame/subscribe [::view-editor.subs/open?])
        mode @(re-frame/subscribe [::view-editor.subs/mode])
        saving? @(re-frame/subscribe [::view-editor.subs/saving?])
        error @(re-frame/subscribe [::view-editor.subs/error])
        form @(re-frame/subscribe [::view-editor.subs/form])
        source-entity @(re-frame/subscribe [::view-editor.subs/entity-type])]
    [modal/bootstrap-modal
     {:is-open? open?
      :on-request-close #(do (track-action! "Filter Editor Cancelled")
                             (re-frame/dispatch [::events/close]))
      :close-button? true
      :centered? true
      :scrollable? true
      :class "view-editor-modal"}
     [:div.view-editor-modal-title-area
      [:div.view-editor-modal-header-row
       [:span.view-editor-modal-header-icon [icons/mux-tab-filter]]
       [:div
        [:h2.view-editor-modal-title
         (get-translation "view-editor.title" "Filter Editor")
         (when source-entity [:span.view-editor-modal-entity " | " source-entity])]
        [:span.view-editor-modal-subtitle
         (get-translation "view-editor.subtitle"
                          "Filters and columns are saved together as a reusable filter.")]]]
]
     [:<>
      (when error [:div.view-editor-error error])
      [view-editor-body]]
     [:div.view-editor-footer
      [buttons/button {:text (get-translation "view-editor.cancel" "Cancel")
                       :on-click #(do (track-action! "Filter Editor Cancelled")
                                     (re-frame/dispatch [::events/close]))}]
      (let [disable-reason @(re-frame/subscribe [::view-editor.subs/save-disabled-reason])
            tooltip (when disable-reason
                      (case disable-reason
                        :empty-name (get-translation "view-editor.save-disabled.empty-name"
                                                     "Name cannot be empty")
                        :incomplete (get-translation "view-editor.save-disabled.incomplete"
                                                     "Please select a value for all fields")
                        :invalid-ptql (get-translation "view-editor.save-disabled.invalid-ptql"
                                                       "Fix the filter syntax error before saving")
                        :no-auto-filter (get-translation "view-editor.save-disabled.no-auto-filter"
                                                         "Select at least one auto-filter field")))]
        [buttons/button {:primary? true
                         :text (if saving?
                                 (get-translation "view-editor.saving" "Saving...")
                                 (get-translation "view-editor.save" "Save"))
                         :tooltip tooltip
                         :disabled? (or saving? (some? disable-reason))
                         :on-click #(do (track-action! "Filter Editor Saved")
                                       (re-frame/dispatch [::events/save]))}])]]))

(comment
  (re-frame/dispatch [::events/open-edit 21625])

  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 4"
                                                   :view-ptql "Priority = \"High\""}}])
  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 5"
                                                   :view-ptql "LINKED TO TestSet"}}])
  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 5"
                                                   :view-ptql "COUNT(LINKED TO TestSet WHERE ('Run Status' = \"BLOCKED\")) > 0"}}]))
